import { Component, ElementRef, ViewEncapsulation, input, model, viewChild, output } from '@angular/core';
import { NgClass } from '@angular/common';

import { ApIconComponent } from '@ap/icon';
import { ApBadgeComponent } from '@ap/badge';

import { ApFloatingContentModalComponent } from './floating-content-modal/floating-content-modal.component';
import { FloatingActionButtonModel } from './floating-action-button.model';

/**
 * This is the Floating Action Button Component. It is always floating on the bottom of the page and opens a content-modal in which you can add content
 **/
@Component({
    selector: 'ap-floating-action-button',
    templateUrl: './floating-action-button.component.html',
    styleUrls: ['./floating-action-button.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, ApFloatingContentModalComponent, ApIconComponent, ApBadgeComponent]
})
export class ApFloatingActionButtonComponent {
    /**
     * Floating content modal element
     * @internal
     */
    private readonly modal = viewChild<ApFloatingContentModalComponent>('modal');

    /**
     * Button ElementRef
     * @internal
     */
    private readonly button = viewChild<ElementRef>('button');

    protected readonly floatingActionButtonModel = FloatingActionButtonModel;

    /**
     * Set the title of the chat container
     */
    public readonly title = input<string>(undefined);

    /**
     * Set a badge number which is shown on the button
     */
    public readonly badgeNumber = input<number>(undefined);

    /**
     * Determines if the modal should be open or closed
     */
    public readonly open = model<boolean>(undefined);

    /**
     * HTMLElement to focus after opening (focuses on modal if not specified)
     * Can be an HTMLElement or any instance that implements `.focus()`
     */
    readonly focusTarget = input<HTMLElement>(undefined);

    /**
     * emits the state of the modal whether it's open or minimized
     */
    readonly openedOrClosed = output<FloatingActionButtonModel>();

    /**
     * Programmatically open or close modal
     */
    public openOrClosedModal(status: FloatingActionButtonModel) {
        this.open.set(status === FloatingActionButtonModel.OPEN);
        this.openedOrClosed.emit(status);
    }

    /**
     * Set focus on correct button since currently focused button is hidden
     * Otherwise focus will move back to body element
     */
    public transitionEnd(): void {
        if (this.open()) {
            (this.focusTarget() || this.modal()).focus();
            return;
        }

        this.button().nativeElement.focus();
    }
}
